import { chamadoNoServico } from './arvoreDeServicos';
import {
    VALOR_AGENTE_LOGADO,
    VALOR_GRUPO_DO_AGENTE,
    VALOR_SEM_RESPONSAVEL,
    VALOR_STATUS_NAO_CONCLUIDO,
} from './filtroDinamico';
import { NOMES_FINALIZADOS, ehConcluido } from './statusDoChamado';
import { linhaDoProduto } from './linhaDoProduto';
import { casaSelecao, comoLista } from './selecaoMultipla';
/** Utilitários puros reutilizados na listagem de tickets (TicketPage, ClientTicketsPage). */

const getTicketStatusName = (ticket) =>
    ticket.ticket_status?.name ?? ticket.ticketStatus?.name ?? null;

// Produto e Representante vêm da empresa (company) do cliente do ticket.
const getTicketCompany = (ticket) => {
    const tc = Array.isArray(ticket?.ticket_clients) ? ticket.ticket_clients : [];
    for (const c of tc) { if (c?.company) return c.company; }
    return ticket?.company || null;
};
const getTicketProduct = (ticket) => getTicketCompany(ticket)?.product_description || '';
const getTicketRepresentative = (ticket) => getTicketCompany(ticket)?.service_group_description || '';

const rawLegacyStatusColumn = (ticket) => {
    if (ticket.status == null || ticket.status === '') return null;
    const n = Number(ticket.status);
    return Number.isNaN(n) ? null : n;
};

/**
 * Resolvido, fechado ou cancelado (nome do status ou código legado 0/3/4).
 *
 * A lista de nomes vem de `statusDoChamado` -- era a QUARTA cópia escrita à
 * mão do mesmo array no projeto. Os códigos legados ficam: esta função é
 * usada para a data de fechamento exibida, onde o `status` antigo ainda
 * responde por chamados velhos.
 */
export const isTicketTerminalClosed = (ticket) => {
    const name = getTicketStatusName(ticket);
    if (name != null && NOMES_FINALIZADOS.includes(name)) return true;
    const legacy = rawLegacyStatusColumn(ticket);
    if (legacy === 0 || legacy === 3 || legacy === 4) return true;
    return false;
};

/** Data exibida como fechamento: resolução quando existir; senão última atualização em status terminal. */
export const getTicketClosingDate = (ticket) => {
    if (ticket.resolved_at != null && ticket.resolved_at !== '') {
        return ticket.resolved_at;
    }
    if (isTicketTerminalClosed(ticket)) {
        const updated = ticket.updated_at;
        return updated != null && updated !== '' ? updated : null;
    }
    return null;
};

/**
 * O DEPARTAMENTO responsável pelo chamado.
 *
 * Normalmente é o grupo de quem está com ele. Sem responsável, é o
 * `service_group_id` do próprio chamado -- a coluna que "atribuir ao
 * departamento" grava desde 19/10/2026, justamente zerando o `assigned_to`.
 *
 * ## Por que o degrau existe
 *
 * Sem ele, o navegador descartava os chamados que o servidor tinha acabado de
 * mandar. Medido em produção em 23/10, no "Kanban - Comercial": a faixa
 * Comercial mostrava "2 ticket(s)" e as colunas "0/2" e "0/1"; a requisição
 * `assigned_to=unassigned:5` voltava "2 de 2" com HTTP 200.
 *
 * O servidor tem uma licença que o navegador não tinha. Pedindo a coluna de um
 * grupo sem responsável, o kanban manda `incluir_sem_responsavel=1`, e
 * `FilterService` passa a admitir chamado sem responsável na condição de grupo
 * de atendimento. O navegador reaplicava a MESMA condição sem essa licença:
 * `assigned_to` nulo não tem grupo, `service_group_id equals 5` dava falso, e
 * os dois chamados sumiam entre a resposta e a tela.
 *
 * Uma regra escrita em dois lugares, divergindo em silêncio -- e por isso o
 * conserto é aqui, na função que TODOS leem, e não num ramo do filtro do
 * kanban. Um segundo caminho recriaria a divergência que causou isto.
 *
 * O degrau muda, de propósito, o que a coluna "Grupo (resp.)" e a exportação
 * mostram num chamado de departamento: antes "—", agora o departamento. É o
 * que a faixa do quadro e a caixa de Responsável já dizem desde 23/10.
 */
export const getTicketAssigneeServiceGroupId = (ticket) => {
    const doResponsavel =
        ticket?.assigned_to?.service_group_id ??
        ticket?.assigned_to?.service_group?.id ??
        null;

    if (doResponsavel != null && doResponsavel !== '') return doResponsavel;

    // Só quando NÃO há responsável. Com responsável, o grupo é o dele, e é
    // assim que o quadro sempre funcionou: um chamado pode ter responsável de
    // um departamento e `service_group_id` de outro (transferido e assumido),
    // e ali quem manda é quem está com ele.
    const responsavel = ticket?.assigned_to;
    const temResponsavel = responsavel != null && responsavel !== ''
        && (typeof responsavel !== 'object' || responsavel?.id != null);

    if (temResponsavel) return null;

    const doChamado = ticket?.service_group_id;

    return doChamado != null && doChamado !== '' ? doChamado : null;
};

const getPrimaryTicketClientLink = (ticket) => {
    const list = ticket?.ticket_clients ?? ticket?.ticketClients;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.find((tc) => tc?.is_primary) ?? list[0];
};

export const getTicketClientRegistration = (ticket) => {
    const list = ticket?.ticket_clients ?? ticket?.ticketClients;
    if (Array.isArray(list)) {
        for (const tc of list) {
            const cc = tc?.client?.code;
            if (cc != null && String(cc).trim() !== '') return String(cc).trim();
            const coc = tc?.company?.code;
            if (coc != null && String(coc).trim() !== '') return String(coc).trim();
        }
    }
    const directClient = ticket?.clientRelation ?? ticket?.client_entity;
    if (directClient?.code != null && String(directClient.code).trim() !== '') {
        return String(directClient.code).trim();
    }
    return '';
};

export const getTicketClientNameLabel = (ticket) => {
    const raw = typeof ticket?.client === 'string' ? ticket.client.trim() : '';
    if (raw) return raw.replace(/\s*\(Cliente Padrão\)\s*$/i, '').trim() || raw;

    const tc0 = getPrimaryTicketClientLink(ticket);
    if (tc0) {
        const n = (tc0?.client?.name || '').trim();
        if (n) return n.replace(/\s*\(Cliente Padrão\)\s*$/i, '').trim() || n;
        const biz = (tc0?.company?.business_name || '').trim();
        if (biz) return biz;
        return (tc0?.company?.name || '').trim();
    }

    const company = ticket?.company;
    if (company) {
        const biz = (company.business_name || '').trim();
        if (biz) return biz;
        return (company.name || '').trim();
    }

    return '';
};

export const getTicketClientRepresentativeType = (ticket) => {
    const tc0 = getPrimaryTicketClientLink(ticket);
    const clientName =
        (tc0?.client?.name || '').trim() ||
        (typeof ticket?.client === 'string' ? ticket.client.trim() : '');

    if (clientName.includes('(Cliente Padrão)')) return 'Cliente Padrão';
    if (tc0?.client_id != null) return 'Cliente';
    if (tc0?.company_id != null || ticket?.company_id != null) return 'Empresa';
    if (clientName) return 'Cliente';
    return '';
};

export const getTicketStatusExportLabel = (ticket, mapStatus) => {
    const name = getTicketStatusName(ticket);
    if (name) return name;
    if (typeof mapStatus === 'function') return mapStatus(ticket.status) || '';
    return '';
};

export const formatTicketClientDisplay = (ticket) => {
    const name = getTicketClientNameLabel(ticket);
    const reg = getTicketClientRegistration(ticket);
    if (name && reg) return `${reg} — ${name}`;
    if (name) return name;
    if (reg) return reg;
    return '';
};

/**
 * Tudo por que a pesquisa da listagem pode achar o cliente de um chamado.
 *
 * ## Por que não basta o rótulo
 *
 * `formatTicketClientDisplay` escolhe UM nome, por uma ordem de preferência
 * (o texto solto do chamado, depois o contato, depois a razão social). Isso
 * está certo para EXIBIR -- a coluna tem uma linha só --, e errado para
 * PROCURAR: digitar a razão social de uma empresa cujo chamado mostra o nome
 * do contato não acharia nada.
 *
 * E o servidor passou a procurar nos três (`Busca\BuscaDeChamados`: registro,
 * razão social e nome fantasia da empresa). Com o navegador olhando só um
 * deles, o servidor baixaria a linha certa e a tela a esconderia -- o modo de
 * falha silencioso de sempre, com o agravante de que a pessoa vê a busca
 * "não funcionar" justamente no caso que o conserto do servidor resolveu.
 *
 * Aqui é tudo junto e sem escolha: é um palheiro, não um rótulo.
 *
 * @param {object} ticket
 * @returns {string}
 */
export const textoDeBuscaDoCliente = (ticket) => {
    const lista = ticket?.ticket_clients ?? ticket?.ticketClients;
    const vinculos = Array.isArray(lista) ? lista : [];

    const pedacos = [
        getTicketClientRegistration(ticket),
        typeof ticket?.client === 'string' ? ticket.client : '',
        ticket?.company?.business_name,
        ticket?.company?.name,
        ticket?.company?.code,
    ];

    vinculos.forEach((vinculo) => {
        pedacos.push(
            vinculo?.client?.name,
            vinculo?.company?.business_name,
            vinculo?.company?.name,
            vinculo?.company?.code
        );
    });

    return pedacos
        .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
        .map((p) => String(p).trim())
        .join(' ');
};

/**
 * O chamado é DESTE cliente, para o filtro da Busca Avançada?
 *
 * Compara por TEXTO -- razão social (ou nome) e registro procurados no rótulo
 * do cliente do chamado --, e não por id. É de propósito: o chamado guarda o
 * vínculo de várias formas (`ticket_clients`, `company`, o texto solto em
 * `ticket.client`), e o filtro precisa achar o cliente em todas elas. É a
 * mesma razão por que `client` nunca virou condição de servidor: lá só existe
 * a coluna `client`, que seria mais estreita.
 *
 * Nome E registro precisam casar quando os dois existem. Só o registro casaria
 * "1405" com qualquer protocolo que contivesse esses dígitos.
 *
 * Extraído de dentro do laço para aceitar MAIS DE UM cliente marcado: com a
 * comparação inline, acrescentar o segundo cliente significaria repetir estas
 * quatro linhas -- e repetir é como as duas cópias da busca do Histórico
 * acabaram divergindo.
 *
 * @param {object} ticket
 * @param {object} cliente  o cliente escolhido no modal
 * @returns {boolean}
 */
export const chamadoDoCliente = (ticket, cliente) => {
    if (!cliente) return false;

    const nome = cliente.company?.business_name || cliente.company?.name || cliente.name || '';
    const registro = cliente.code != null ? String(cliente.code) : '';
    const procurado = `${formatTicketClientDisplay(ticket)} ${ticket?.client || ''}`.toLowerCase();

    if (nome && !procurado.includes(nome.toLowerCase())) return false;
    if (registro && !procurado.includes(registro.toLowerCase())) return false;

    return true;
};

/**
 * Tickets vinculados ao cliente (principal, ticket_clients ou mesma empresa).
 */
export const ticketBelongsToClient = (ticket, clientId, companyId) => {
    const cid = clientId != null ? Number(clientId) : NaN;
    if (Number.isNaN(cid) || cid <= 0) return false;
    const compId = companyId != null && companyId !== '' ? Number(companyId) : null;

    if (ticket.client_id != null && Number(ticket.client_id) === cid) return true;

    const list = ticket.ticket_clients;
    if (Array.isArray(list)) {
        for (const tc of list) {
            if (tc.client_id != null && Number(tc.client_id) === cid) return true;
            if (compId != null && !Number.isNaN(compId) && tc.company_id != null && Number(tc.company_id) === compId) {
                return true;
            }
        }
    }

    if (compId != null && !Number.isNaN(compId) && ticket.company_id != null && Number(ticket.company_id) === compId) {
        return true;
    }

    return false;
};

/**
 * @param {object|null} usuarioAtual  quem está olhando. Necessário para os
 *   valores dinâmicos ("Agente logado", "Grupo do agente logado"), que aqui
 *   são resolvidos NO NAVEGADOR -- ao contrário do filtro salvo, que o
 *   servidor resolve. São mecanismos diferentes porque são coisas diferentes:
 *   a busca avançada é da sessão de quem olha e nunca é compartilhada, então
 *   não existe outra pessoa para quem o valor precisasse significar outra
 *   coisa. O vocabulário é o mesmo de propósito.
 *
 *   Omitir o argumento mantém o comportamento antigo para todo filtro que não
 *   usa valor dinâmico -- e faz os dinâmicos não casarem com nada, em vez de
 *   deixarem passar tudo.
 */
export const applyAdvancedSearchFilters = (
    tickets,
    filters,
    usuarioAtual = null,
    // Os dois últimos chegaram por caminhos diferentes e ambos são
    // opcionais: quem não os passa (a `ClientTicketsPage` não passa o
    // primeiro) continua vendo exatamente o que via.
    finalizados = [],
    paiPorIdDoServico = new Map(),
) => {
    if (!filters) return tickets;

    return tickets.filter((ticket) => {
        if (filters.title && !ticket.title?.toLowerCase().includes(filters.title.toLowerCase())) {
            return false;
        }

        if (filters.status !== '' && filters.status !== null && filters.status !== undefined) {
            if (filters.status === VALOR_STATUS_NAO_CONCLUIDO) {
                // `ehConcluido`, e NÃO `isTicketTerminalClosed`: aquela
                // também aceita o `status` legado 0/3/4, e isto aqui precisa
                // dar a mesma resposta que o servidor dá no filtro salvo.
                // Duas noções de "concluído" na mesma tela divergiriam em
                // algum chamado antigo, e ninguém saberia qual está certa.
                if (ehConcluido(ticket, finalizados)) {
                    return false;
                }
            } else {
                // filters.status guarda o status_id real (não o status legado).
                const ticketStatusId = ticket.status_id ?? ticket.ticket_status?.id ?? ticket.ticketStatus?.id ?? null;
                if (ticketStatusId == null || Number(ticketStatusId) !== Number(filters.status)) {
                    return false;
                }
            }
        }

        // Prioridade, categoria e canal aceitam MAIS DE UMA opção marcada
        // (ponto 11 da Gisele). `casaSelecao` trata os três casos: lista,
        // string da escolha única antiga e campo em branco.
        if (!casaSelecao(ticket.priority, filters.priority)) {
            return false;
        }

        if (filters.product) {
            if (getTicketProduct(ticket) !== filters.product) {
                return false;
            }
        }

        // LINHA do produto (K3): Sysfar, Max, Next. Diferente do `product`
        // acima, que compara a descrição CRUA do ERP ("MAX Ouro",
        // "MAX Ilimitado") e por isso obriga a escolher uma por vez.
        //
        // A regra vem de `utils/linhaDoProduto`, a MESMA que desenha o chip do
        // cliente — não há segunda regra em JavaScript. A cópia em PHP
        // (`Company::linhaDoProduto`) carrega os mesmos casos de teste.
        //
        // Chamado sem empresa ou sem produto não casa com nenhuma linha:
        // `linhaDoProduto` devolve `null` e a comparação falha. Deixar passar
        // mostraria a lista inteira como se estivesse refinada.
        if (filters.linhaDoProduto) {
            if (linhaDoProduto(getTicketCompany(ticket)) !== filters.linhaDoProduto) {
                return false;
            }
        }

        if (filters.representative) {
            if (getTicketRepresentative(ticket) !== filters.representative) {
                return false;
            }
        }

        if (filters.assignedTo) {
            const escolhido = filters.assignedTo.id;

            if (escolhido === VALOR_SEM_RESPONSAVEL) {
                // `assigned_to` chega como objeto quando há responsável e
                // como nulo quando não há -- por isso basta olhar o id.
                if (ticket.assigned_to?.id != null) return false;
            } else if (escolhido === VALOR_AGENTE_LOGADO) {
                // Resolvido NO NAVEGADOR, ao contrário do filtro salvo, que o
                // servidor resolve. São dois mecanismos diferentes porque são
                // duas coisas diferentes: a busca avançada é da sessão de
                // quem está olhando e nunca é compartilhada, então não há
                // outra pessoa para quem o valor precisasse significar outra
                // coisa. O vocabulário é o mesmo de propósito -- a pessoa vê
                // a mesma opção nos dois lugares.
                if (usuarioAtual?.id == null) return false;
                if (Number(ticket.assigned_to?.id) !== Number(usuarioAtual.id)) return false;
            } else if (ticket.assigned_to?.id !== escolhido) {
                return false;
            }
        }

        /*
         * O departamento aceita MAIS DE UM (ponto 11 da Gisele). Antes era
         * um valor só; o estado antigo, que volta do `localStorage` de quem
         * já usava a busca, é uma string -- `comoLista` normaliza os dois.
         *
         * O marcador dinâmico convive com os ids na mesma lista: "o meu
         * departamento OU o Comercial" é uma escolha legítima.
         */
        const gruposEscolhidos = comoLista(filters.serviceGroupId);
        if (gruposEscolhidos.length > 0) {
            const doChamado = getTicketAssigneeServiceGroupId(ticket);
            const casaUmDosGrupos = (escolhido) => {
                // O grupo de quem está olhando. Fora disso, `Number(marcador)`
                // seria `NaN`, e tratá-lo como id comum faria esta opção não
                // casar com nada.
                if (escolhido === VALOR_GRUPO_DO_AGENTE) {
                    const meuGrupo = usuarioAtual?.service_group_id;

                    if (meuGrupo == null) return false;

                    return doChamado != null && Number(doChamado) === Number(meuGrupo);
                }

                const want = Number(escolhido);

                if (Number.isNaN(want)) return false;

                return doChamado != null && Number(doChamado) === want;
            };

            /*
             * Valor que não é id nem marcador é IGNORADO, e não "não casa":
             * era o que a versão de escolha única fazia (`if
             * (!Number.isNaN(want))`), e trocar isso por uma lista vazia na
             * tela seria esconder chamado por causa de um estado estranho.
             */
            const utilizaveis = gruposEscolhidos.filter(
                (e) => e === VALOR_GRUPO_DO_AGENTE || !Number.isNaN(Number(e))
            );

            // SÓ recusa, nunca aprova de uma vez: um `return true` aqui
            // pularia todas as condições seguintes (criado por, datas) e o
            // chamado passaria por elas sem ser conferido.
            if (utilizaveis.length > 0 && !utilizaveis.some(casaUmDosGrupos)) {
                return false;
            }
        }

        if (filters.createdBy) {
            if (filters.createdBy.id === VALOR_AGENTE_LOGADO) {
                if (usuarioAtual?.id == null) return false;
                if (Number(ticket.created_by?.id) !== Number(usuarioAtual.id)) return false;
            } else if (ticket.created_by?.id !== filters.createdBy.id) {
                return false;
            }
        }

        // MAIS DE UM cliente marcado (ponto 11 da Gisele, continuação).
        // `comoLista` trata o estado antigo -- um objeto só, que é o que volta
        // do `localStorage` de quem já usava a busca avançada.
        const clientesEscolhidos = comoLista(filters.client);
        if (clientesEscolhidos.length > 0
            && !clientesEscolhidos.some((cliente) => chamadoDoCliente(ticket, cliente))) {
            return false;
        }

        /*
         * `filters.textoNaAcao` NÃO é avaliado aqui, e é de propósito.
         *
         * A listagem traz o chamado, não as ações dele -- não há texto de ação
         * nenhum neste objeto para procurar. Quem aplica essa condição é o
         * SERVIDOR (`FilterService::aplicarCondicaoDeTextoNaAcao`), e a tela,
         * ao ver o campo preenchido, passa a montar a lista a partir do
         * resultado dele.
         *
         * Inventar uma avaliação aqui -- procurar no título, na descrição --
         * seria pior que não ter: o filtro passaria a significar uma coisa no
         * servidor e outra na tela, e a lista mudaria conforme o chamado
         * tivesse vindo de um caminho ou do outro.
         */

        if (!casaSelecao(ticket.category, filters.category, { ignorarCaixa: true })) {
            return false;
        }

        // Cada serviço marcado inclui a sua DESCENDÊNCIA, igual ao filtro
        // salvo e igual ao servidor. Antes aqui era igualdade exata, e isso
        // passou a importar quando o serviço virou condição de servidor:
        // "filtrar por Suporte" não pode significar duas coisas diferentes na
        // mesma tela.
        //
        // Sem o mapa (quem não o passa, ou enquanto a árvore carrega),
        // `chamadoNoServico` cai na igualdade exata -- exatamente o
        // comportamento anterior.
        const servicosEscolhidos = comoLista(filters.serviceId);
        if (servicosEscolhidos.length > 0
            && !servicosEscolhidos.some((id) => chamadoNoServico(ticket, id, paiPorIdDoServico))) {
            return false;
        }

        if (filters.contact && filters.contact !== '') {
            if (!ticket.contact || !ticket.contact.toLowerCase().includes(filters.contact.toLowerCase())) {
                return false;
            }
        }

        if (!casaSelecao(ticket.channel, filters.channel, { ignorarCaixa: true })) {
            return false;
        }

        // Última ação: COMPARA o veredito do servidor, não o recalcula.
        //
        // A regra de quem é cliente e quem é operador mora em
        // `UltimaAcaoDoChamado` (api) e chega pronta em `ticket.ultima_acao`,
        // pelos dois endpoints que alimentam a grade. Reimplementá-la aqui
        // criaria a segunda fonte que já nos custou três defeitos silenciosos.
        //
        // Linha SEM o campo não passa. É o inverso do padrão dos filtros acima,
        // e de propósito: se o payload não trouxer o veredito, deixar passar
        // mostraria a lista inteira como se estivesse refinada. Não casar é
        // visível; deixar passar, não.
        if (filters.ultimaAcao && filters.ultimaAcao !== '') {
            if (ticket.ultima_acao !== filters.ultimaAcao) {
                return false;
            }
        }

        if (filters.ccUsers) {
            const ticketCcUsers = ticket.cc_users || [];
            if (!ticketCcUsers.includes(filters.ccUsers.id)) {
                return false;
            }
        }

        if (filters.createdAtStart) {
            const ticketCreatedAt = new Date(ticket.created_at);
            const filterStart = new Date(filters.createdAtStart);
            filterStart.setHours(0, 0, 0, 0);
            if (ticketCreatedAt < filterStart) {
                return false;
            }
        }

        if (filters.createdAtEnd) {
            const ticketCreatedAt = new Date(ticket.created_at);
            const filterEnd = new Date(filters.createdAtEnd);
            filterEnd.setHours(23, 59, 59, 999);
            if (ticketCreatedAt > filterEnd) {
                return false;
            }
        }

        if (filters.resolvedAtStart) {
            if (!ticket.resolved_at) return false;
            const ticketResolvedAt = new Date(ticket.resolved_at);
            const filterStart = new Date(filters.resolvedAtStart);
            filterStart.setHours(0, 0, 0, 0);
            if (ticketResolvedAt < filterStart) {
                return false;
            }
        }

        if (filters.resolvedAtEnd) {
            if (!ticket.resolved_at) return false;
            const ticketResolvedAt = new Date(ticket.resolved_at);
            const filterEnd = new Date(filters.resolvedAtEnd);
            filterEnd.setHours(23, 59, 59, 999);
            if (ticketResolvedAt > filterEnd) {
                return false;
            }
        }

        if (filters.updatedAtStart) {
            const ticketUpdatedAt = new Date(ticket.updated_at);
            const filterStart = new Date(filters.updatedAtStart);
            filterStart.setHours(0, 0, 0, 0);
            if (ticketUpdatedAt < filterStart) {
                return false;
            }
        }

        if (filters.updatedAtEnd) {
            const ticketUpdatedAt = new Date(ticket.updated_at);
            const filterEnd = new Date(filters.updatedAtEnd);
            filterEnd.setHours(23, 59, 59, 999);
            if (ticketUpdatedAt > filterEnd) {
                return false;
            }
        }

        return true;
    });
};
